import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Loader2 } from "lucide-react";
import { api, DashboardDemographicBucket, DashboardRange, DashboardStats } from "../api";
import { PageHeader } from "../components/PageHeader";
import { useScope } from "../components/ScopeContext";

const RANGES: { id: DashboardRange; label: string }[] = [
  { id: "24h", label: "24 hours" },
  { id: "7d", label: "7 days" },
  { id: "30d", label: "30 days" },
  { id: "all", label: "All time" },
];

function formatBucketLabel(ms: number, range: DashboardRange): string {
  const d = new Date(ms);
  if (range === "24h") {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  }
  if (range === "all") {
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  }
  return d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" });
}

function DemographicBars({
  title,
  rows,
  total,
}: {
  title: string;
  rows: DashboardDemographicBucket[];
  total: number;
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));

  return (
    <div className="bf-card p-4">
      <h3 className="mb-3 text-center text-sm font-medium text-white">{title}</h3>
      {rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-bf-muted">No data in this range.</p>
      ) : (
        <div className="space-y-2.5">
          {rows.map((row) => {
            const pct = total > 0 ? Math.round((row.count / total) * 100) : 0;
            const muted = row.label === "Unknown";
            return (
              <div key={row.label}>
                <div className="mb-1 flex items-center justify-between text-xs">
                  <span className={muted ? "text-bf-muted" : "text-white/90"}>{row.label}</span>
                  <span className="tabular-nums text-bf-muted">
                    {row.count.toLocaleString()} ({pct}%)
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-white/10">
                  <div
                    className={
                      "h-full rounded-full " +
                      (muted ? "bg-white/25" : "bg-bf-violet/80")
                    }
                    style={{ width: `${(row.count / max) * 100}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const CHART_W = 800;
const CHART_H = 160;
const CHART_PAD = { top: 12, right: 12, bottom: 4, left: 4 };

function TimeChart({ stats }: { stats: DashboardStats }) {
  const buckets = stats.over_time;

  if (buckets.length === 0 || buckets.every((b) => b.count === 0)) {
    return (
      <div className="bf-card p-4">
        <h3 className="mb-3 text-center text-sm font-medium text-white">Customers over time</h3>
        <p className="py-4 text-center text-sm text-bf-muted">No data in this range.</p>
      </div>
    );
  }

  const max = Math.max(1, ...buckets.map((b) => b.count));
  const plotW = CHART_W - CHART_PAD.left - CHART_PAD.right;
  const plotH = CHART_H - CHART_PAD.top - CHART_PAD.bottom;

  const points = buckets.map((bucket, i) => {
    const x =
      buckets.length === 1
        ? CHART_PAD.left + plotW / 2
        : CHART_PAD.left + (i / (buckets.length - 1)) * plotW;
    const y = CHART_PAD.top + plotH - (bucket.count / max) * plotH;
    return { x, y, bucket };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${CHART_PAD.top + plotH} L ${points[0].x} ${CHART_PAD.top + plotH} Z`;

  const yTicks = [0, 0.5, 1].map((t) => ({
    y: CHART_PAD.top + plotH - t * plotH,
    label: Math.round(max * t).toLocaleString(),
  }));

  return (
    <div className="bf-card p-4">
      <h3 className="mb-3 text-center text-sm font-medium text-white">Customers over time</h3>
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className="h-40 w-full"
        role="img"
        aria-label="Customers over time line chart"
      >
        <defs>
          <linearGradient id="bf-line-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="rgb(192 38 211 / 0.35)" />
            <stop offset="100%" stopColor="rgb(192 38 211 / 0)" />
          </linearGradient>
        </defs>

        {yTicks.map((tick) => (
          <g key={tick.y}>
            <line
              x1={CHART_PAD.left}
              y1={tick.y}
              x2={CHART_W - CHART_PAD.right}
              y2={tick.y}
              stroke="rgb(255 255 255 / 0.08)"
              strokeWidth={1}
            />
          </g>
        ))}

        <path d={areaPath} fill="url(#bf-line-fill)" />
        <path
          d={linePath}
          fill="none"
          stroke="rgb(192 38 211)"
          strokeWidth={2.5}
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {points.map(({ x, y, bucket }) => (
          <g key={bucket.bucket_start_ms}>
            <circle cx={x} cy={y} r={4} fill="rgb(192 38 211)" stroke="rgb(15 15 25)" strokeWidth={2}>
              <title>
                {formatBucketLabel(bucket.bucket_start_ms, stats.range)}: {bucket.count.toLocaleString()}
              </title>
            </circle>
          </g>
        ))}
      </svg>
      <div className="mt-2 flex justify-between text-[10px] text-bf-muted sm:text-xs">
        <span>{formatBucketLabel(buckets[0].bucket_start_ms, stats.range)}</span>
        <span>{formatBucketLabel(buckets[buckets.length - 1].bucket_start_ms, stats.range)}</span>
      </div>
    </div>
  );
}

export default function Insights() {
  const [range, setRange] = useState<DashboardRange>("7d");
  const { scope, scopeParams, scopeKey } = useScope();

  const cameras = useQuery({
    queryKey: ["cameras", scopeKey],
    queryFn: () => api.cameras.list(scopeParams),
    enabled: scope.kind !== "all",
    staleTime: 60_000,
  });
  const scopedIds =
    scope.kind === "all" ? undefined : (cameras.data ?? []).map((c) => c.id);

  const statsQuery = useQuery({
    queryKey: ["insights", range, scopeKey, (scopedIds ?? []).join(",")],
    queryFn: () => api.dashboard.stats({ range, camera_ids: scopedIds }),
    enabled: scope.kind === "all" || cameras.isSuccess,
    refetchInterval: 60_000,
  });

  const stats = statsQuery.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Insights"
        icon={BarChart3}
        description="See how many people visited and a breakdown of who they were."
        actions={
          <span className="rounded-full bg-bf-violet/20 px-2 py-0.5 text-xs font-semibold uppercase tracking-wide text-bf-magenta ring-1 ring-bf-violet/40">
            Beta
          </span>
        }
      />

      <div className="flex flex-wrap gap-2">
        {RANGES.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            onClick={() => setRange(id)}
            className={
              "rounded-lg px-3 py-1.5 text-sm transition " +
              (range === id
                ? "bg-bf-violet/30 text-white ring-1 ring-bf-violet/60"
                : "bg-white/5 text-bf-muted hover:bg-white/10 hover:text-bf-text")
            }
          >
            {label}
          </button>
        ))}
      </div>

      {statsQuery.isLoading && (
        <div className="bf-card flex items-center gap-3 p-6 text-bf-muted">
          <Loader2 size={20} className="animate-spin" />
          Loading insights…
        </div>
      )}

      {statsQuery.isError && (
        <div className="bf-card p-4 text-sm text-red-300">Failed to load visitor stats.</div>
      )}

      {stats && (
        <>
          <div className="bf-gradient-border">
            <div className="bf-gradient-border-inner p-6 text-center">
              <div className="text-sm text-bf-muted">Total customers</div>
              <div className="mt-1 text-4xl font-bold tabular-nums text-white sm:text-5xl">
                {stats.total_customers.toLocaleString()}
              </div>
              <div className="mt-1 text-xs text-bf-muted">
                {range === "all"
                  ? "All stored track records"
                  : `Tracks seen in the selected window`}
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <DemographicBars
              title="Sex"
              rows={stats.demographics.sex}
              total={stats.total_customers}
            />
            <DemographicBars
              title="Age group"
              rows={stats.demographics.age_group}
              total={stats.total_customers}
            />
          </div>

          <TimeChart stats={stats} />
        </>
      )}
    </div>
  );
}
